import type { Color, GameState, Move, PieceState, PieceType, Square } from '@ccc/shared-types';
import {
  FILES,
  offsetSquare,
  fileOf,
  fileIndex,
  indexFile,
  rankOf,
  sq,
  pieceAt,
  piecesOf,
} from '@ccc/rule-engine';

export type Offset = [df: number, dr: number];

export function piecesOfColor(state: GameState, color: Color): PieceState[] {
  return piecesOf(state, color);
}

export function makeMove(
  piece: PieceState,
  to: Square,
  state: GameState,
  extra: Partial<Move> = {},
): Move {
  const captured = pieceAt(state, to);
  const m: Move = { from: piece.square, to, pieceId: piece.id, ...extra };
  if (captured && captured.color !== piece.color) m.capturedPieceId = captured.id;
  return m;
}

export function emptyAt(state: GameState, sqr: Square): boolean {
  return !pieceAt(state, sqr);
}

export function friendlyAt(state: GameState, sqr: Square, color: Color): boolean {
  const p = pieceAt(state, sqr);
  return !!p && p.color === color;
}

export function enemyAt(state: GameState, sqr: Square, color: Color): boolean {
  const p = pieceAt(state, sqr);
  return !!p && p.color !== color;
}

export function castSquare(file: string, rank: number): Square {
  return sq(file as any, rank as any);
}

export { FILES, offsetSquare, fileOf, fileIndex, indexFile, rankOf, sq, pieceAt, piecesOf };

/**
 * Generate all squares reachable from `from` along a ray, allowing the ray to
 * jump over exactly one piece. Stops at the first friendly after the vault, and
 * captures the first enemy after the vault.
 */
export function vaultedRay(
  from: Square,
  state: GameState,
  color: Color,
  offsets: Offset[],
): Square[] {
  const out: Square[] = [];
  for (const [df, dr] of offsets) {
    let f = df;
    let r = dr;
    let vaulted = false;
    while (true) {
      const t = offsetSquare(from, f, r);
      if (!t) break;
      const occ = pieceAt(state, t);
      if (!occ) {
        out.push(t);
      } else if (!vaulted) {
        // jump over the first piece
        vaulted = true;
      } else if (occ.color !== color) {
        out.push(t);
        break;
      } else {
        break;
      }
      f += df;
      r += dr;
    }
  }
  return out;
}

export function squaresAround(from: Square, radius: number): Square[] {
  const out: Square[] = [];
  for (let df = -radius; df <= radius; df++) {
    for (let dr = -radius; dr <= radius; dr++) {
      if (df === 0 && dr === 0) continue;
      const t = offsetSquare(from, df, dr);
      if (t) out.push(t);
    }
  }
  return out;
}

export const KNIGHT_OFFSETS: Offset[] = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];

export const ORTHO_OFFSETS: Offset[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

export const DIAG_OFFSETS: Offset[] = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/** Length of a straight (orthogonal or diagonal) move from a to b. 0 = same square. */
export function lineDistance(a: Square, b: Square): number {
  const dfa = Math.abs(fileIndex(fileOf(a)) - fileIndex(fileOf(b)));
  const dra = Math.abs(rankOf(a) - rankOf(b));
  if (dfa === 0 && dra === 0) return 0;
  if (dfa === 0) return dra;
  if (dra === 0) return dfa;
  if (dfa === dra) return dfa;
  return -1; // not a straight line
}

export function isOrthoAdjacent(a: Square, b: Square): boolean {
  const dfa = Math.abs(fileIndex(fileOf(a)) - fileIndex(fileOf(b)));
  const dra = Math.abs(rankOf(a) - rankOf(b));
  return (dfa === 1 && dra === 0) || (dfa === 0 && dra === 1);
}

export function isDiagAdjacent(a: Square, b: Square): boolean {
  const dfa = Math.abs(fileIndex(fileOf(a)) - fileIndex(fileOf(b)));
  const dra = Math.abs(rankOf(a) - rankOf(b));
  return dfa === 1 && dra === 1;
}

export function isAdjacent(a: Square, b: Square): boolean {
  return isOrthoAdjacent(a, b) || isDiagAdjacent(a, b);
}
